/**
 * Current user's own actively-suspended brand ids (`brands.id`, the integer PK — matches the `id`
 * field the brand-list API already returns), from `/affiliate-networks/suspended-brand-ids`. Unlike
 * `cjPidRegistry`'s account gating this has no permissive multi-value default: an anonymous or
 * unfetched viewer simply has zero suspensions, so `[]` is always correct, not a placeholder.
 */
let suspendedBrandIds: number[] = []
/** Affiliate networks (`brands.network` strings) the user is blocked from, from the same endpoint. */
let blockedNetworks: string[] = []

export const setSuspendedBrandIds = (ids: number[]): void => {
  suspendedBrandIds = Array.isArray(ids) ? ids : []
}

export const setBlockedNetworks = (networks: string[]): void => {
  blockedNetworks = Array.isArray(networks) ? networks : []
}

export const clearSuspendedBrandIds = (): void => {
  suspendedBrandIds = []
  blockedNetworks = []
}

/** True when the user is suspended from the brand itself or blocked from the brand's whole network. */
export const isBrandSuspended = (brand?: {
  id?: number | string | null
  network?: string | null
}): boolean => {
  if (!brand) return false
  if (brand.network != null && blockedNetworks.includes(brand.network)) {
    return true
  }
  if (brand.id == null) return false
  return suspendedBrandIds.includes(Number(brand.id))
}

const ACCESS_SUSPENDED_CODES = [
  'BRAND_ACCESS_SUSPENDED',
  'NETWORK_ACCESS_SUSPENDED',
]

/**
 * The backend's message when a share-link / shopping-board write was rejected because the user is
 * suspended from the brand or blocked from its network (403 + machine code); otherwise `undefined`.
 */
export const getAccessSuspendedMessage = (err: any): string | undefined => {
  const { status, data } = err?.response ?? {}
  return status === 403 && ACCESS_SUSPENDED_CODES.includes(data?.code)
    ? data.message
    : undefined
}

/** Hide brands the current user is suspended from. Cosmetic only — the backend enforces on write. */
export const filterBrandsBySuspension = <
  T extends { id?: number | string | null; network?: string | null }
>(
  brands: T[]
): T[] => {
  if (!Array.isArray(brands)) return brands
  if (!suspendedBrandIds.length && !blockedNetworks.length) return brands
  return brands.filter(brand => !isBrandSuspended(brand))
}
